import * as microsoftTeams from '@microsoft/teams-js';
import {
    SET_USER_INFO,
    SET_IS_IN_TEAMS,
    SET_ACCESS_TOKEN,
    SET_TOKEN_ERROR
} from './types';
import { sendToken } from './accessTokenActions';
import { hasValidTokens, clearTokens } from 'utils/authUtils';
import { getPortals } from './apiActions';
import logger from 'utils/logger';

export const initializeTeamsApp = () => async (dispatch) => {
    try {
        // Development mode: Clear tokens on every refresh
        if (process.env.NODE_ENV === 'development') {
            clearTokens();
            logger.log("Development mode: Cleared tokens on refresh");
        }

        // Check if we already have valid tokens
        if (hasValidTokens()) {
            logger.log("Valid tokens found, skipping auth flow");
            dispatch({ type: SET_IS_IN_TEAMS, payload: true });
            await dispatch(getPortals());
            return;
        }

        logger.log("No valid tokens found, starting auth flow");

        // Check if running in Teams environment
        const isInTeams = await microsoftTeams.app.isInitialized() ||
                          (typeof window !== 'undefined' && window.parent !== window);

        if (!isInTeams) {
            // Development mode - not in Teams
            dispatch({
                type: SET_USER_INFO,
                payload: { displayName: 'Development User', userPrincipalName: 'dev@example.com' }
            });
            const devAssertion = process.env.REACT_APP_DEV_ASSERTION;
            if (devAssertion) {
                await dispatch(sendToken("/account/access-token", { accessToken: devAssertion }));
            }

            dispatch({ type: SET_IS_IN_TEAMS, payload: false });
            dispatch({ type: SET_TOKEN_ERROR, payload: 'Not running in Teams context - token unavailable in development mode' });

            logger.log("Development mode: Simulating token retrieval");
            return;
        }

        await microsoftTeams.app.initialize();
        const context = await microsoftTeams.app.getContext();

        dispatch({ type: SET_USER_INFO, payload: context.user || context.account });
        dispatch({ type: SET_IS_IN_TEAMS, payload: true });

        if (context) {
            await getTeamsToken(dispatch);
        }
    } catch (error) {
        logger.error('Teams initialization failed:', error);
        dispatch({ type: SET_TOKEN_ERROR, payload: 'Teams initialization failed' });
    }
};

/**
 * Get token from Teams and exchange it for auth tokens
 * Exported so it can be used by the auth refresh callback
 */
export const getTeamsToken = async (dispatch) => {
    try {
        // Ensure Teams SDK is initialized before attempting to get token
        const isInitialized = microsoftTeams.app.isInitialized();
        if (!isInitialized) {
            logger.log("Teams SDK not initialized, initializing now...");
            await microsoftTeams.app.initialize();
        }

        const token = await microsoftTeams.authentication.getAuthToken();
        dispatch({ type: SET_ACCESS_TOKEN, payload: token });

        try {
            await dispatch(sendToken("/account/access-token", { accessToken :  token }));
            logger.log("Token exchange successful");
        } catch (setTokenError) {
            logger.error("Token exchange failed:", setTokenError);
            throw setTokenError;
        }
    } catch (error) {
        // Try silent authentication
        try {
            logger.log("Attempting silent authentication");
            const silentToken = await microsoftTeams.authentication.getAuthToken({ silent: true });
            dispatch({ type: SET_ACCESS_TOKEN, payload: silentToken });

            try {
                await dispatch(sendToken("/account/access-token", { accessToken: silentToken }));
                logger.log("Silent token exchange successful");
            } catch (setTokenError) {
                logger.error("Silent token exchange failed:", setTokenError);
                throw setTokenError;
            }
        } catch (silentError) {
            logger.error('Silent authentication failed:', silentError);
            clearTokens();
            dispatch({
                type: SET_TOKEN_ERROR,
                payload: `Authentication failed: ${error.message || 'Unable to get user token'}`
            });
            throw silentError;
        }
    }
};
